// Data Pipeline TRACE mode: one item's journey, start to finish. The header says
// where it ended up; the subject strip says what it is, in names; the journey
// lists every recorded step by phase, one selected at a time, with its detail
// beside it (or under it, below 1280 px); "Ticket now" is the ticket as SAM holds
// it at this moment; the footer says exactly what window of evidence this is.
// Nothing here claims more than the records prove: a missing observation is
// never read as "did not happen".

import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { UseQueryResult } from '@tanstack/react-query';
import type {
  BuyerState,
  LineageDestination,
  LineageStage,
  LineageSubject,
  TicketLineState,
} from '@/api/types.gen';
import type { PipelineLineageResponse, TicketCurrentState, TicketEligibility } from '@/api/types';
import { ApiError } from '@/api/errors';
import type { DpNodeId } from '@/lib/flow-layout';
import { dpAge } from '@/lib/flow-layout';
import { fmtDateTime, withEt } from '@/lib/datetime';
import {
  JOURNEY_PHASES,
  OUTCOME_LABEL,
  destinationOutcome,
  foldJourney,
  kindFromCorrelationId,
  outcomeBand,
  phaseLabel,
  reasonWords,
  stageNode,
  statusBand,
  stepSentence,
  traceOutcome,
  traceState,
  uid2Words,
} from '@/lib/flow-lineage';
import type { JourneyStep, LineageKind } from '@/lib/flow-lineage';
import { useAdaptive } from '@/lib/responsive';
import { QueryErrorNote } from '@/components/error-panel';
import { FailureNotice } from '@/components/failure-notice';
import { SyncDot, SyncPill } from '@/components/sync-pill';
import { CopyableId } from '@/components/copyable-id';
import { Icons } from '@/components/primitives';
import { useNowMs } from '@/components/flow-diagram';

const STAGE_LABEL: Record<LineageStage, string> = {
  ingest: 'Ingest',
  enrich: 'Person match',
  enqueue: 'Queued',
  meta_build: 'Meta · build',
  meta_send: 'Meta · send',
  uid2_map: 'UID2 map',
  ttd_build: 'The Trade Desk · build',
  ttd_send: 'The Trade Desk · send',
};

const TARGET_LABEL: Record<string, string> = { meta: 'Meta', ttd: 'The Trade Desk' };

const at = (iso: string) => withEt(fmtDateTime(iso));
const money = (cents: number | null, currency: string | null) =>
  cents == null ? null : `${currency && currency !== 'USD' ? currency + ' ' : '$'}${(cents / 100).toFixed(2)}`;
const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;

/** Line cards shown before "Show all": a ticket has one or two lines almost always. */
const FIRST_LINE_CARDS = 6;

/** The source step of a ticket trace: EncorePro created its first line. */
const SOURCE_STEP = 'source';

// ── Header ──────────────────────────────────────────────────────────────────

function OutcomeChip({ destinations }: { destinations: readonly LineageDestination[] }) {
  const outcome = traceOutcome(destinations);
  const skipReasons =
    outcome === 'skipped'
      ? [...new Set(destinations.map((d) => reasonWords(d.reason)).filter((r): r is string => !!r))]
      : [];
  const label = OUTCOME_LABEL[outcome] + (skipReasons.length ? ` · ${skipReasons.join(', ')}` : '');
  return <SyncPill band={outcomeBand(outcome)} label={label} />;
}

// ── Subject ─────────────────────────────────────────────────────────────────

function lineCounts(ticket: TicketCurrentState): string {
  const parts = [plural(ticket.sale_lines, 'sale line')];
  if (ticket.refund_lines) parts.push(plural(ticket.refund_lines, 'refund line'));
  return parts.join(' · ');
}

/**
 * The fold's verdict on the ticket's CURRENT lines. It says what would be queued
 * now, never what happened before: a purchase delivered and later refunded is
 * delivered (Deliveries) and, from its current lines, no longer a purchase.
 */
export function eligibilityWords(e: TicketEligibility): string {
  if (e.eligible) {
    const amount = money(e.net_amount_cents, e.currency);
    return `Counts as ${plural(e.net_units, 'net purchase')}${amount ? ` of ${amount}` : ''}.`;
  }
  switch (e.reason) {
    case 'no_amount':
      return 'Would not be queued: no line carries an amount.';
    case 'no_net_units':
      return 'Would not be queued: refunds cancel every sale.';
    case 'negative_net':
      return `Would not be queued: the net amount is ${money(e.net_amount_cents, e.currency) ?? 'below zero'}.`;
    default:
      return 'Would not be queued.';
  }
}

function SubjectStrip({
  subject,
  ticket,
  kind,
}: {
  subject: LineageSubject | null | undefined;
  ticket: TicketCurrentState | null | undefined;
  kind: LineageKind;
}) {
  if (!subject && !ticket) return null;
  const production = subject?.productions_mixed
    ? 'Several productions'
    : subject?.production_name ??
      (subject?.production_id ? `Unknown production (${subject.production_id.slice(0, 8)}…)` : null);
  const venue = [subject?.venue_name, subject?.venue_city].filter(Boolean).join(' · ');
  return (
    <div className="dp-subject" data-testid="trace-subject">
      <div className="dp-subject-main">
        {production && (
          <span className="dp-subject-prod">
            {subject?.production_id && !subject.productions_mixed ? (
              <Link to="/backend/pipeline" search={{ focus: subject.production_id }} title="Open this production's ingest">
                {production}
              </Link>
            ) : (
              production
            )}
          </span>
        )}
        {venue && <span className="dp-subject-venue">{venue}</span>}
        {subject?.performance_at && <span className="dp-subject-perf">Performance {at(subject.performance_at)}</span>}
      </div>
      <div className="dp-subject-facts">
        {kind === 'ticket' && (subject?.orders_mixed || subject?.order_id) && (
          <span className="dp-subject-fact">
            <span className="k">Order</span>
            {subject.orders_mixed ? 'several orders' : <CopyableId value={subject.order_id!} truncate/>}
          </span>
        )}
        {ticket && (
          <span className="dp-subject-fact">
            <span className="k">Lines</span>
            {lineCounts(ticket)}
          </span>
        )}
        {ticket && (
          <span className={'dp-subject-fact' + (ticket.eligibility.eligible ? '' : ' warn')}>
            <span className="k">From current lines</span>
            {eligibilityWords(ticket.eligibility)}
          </span>
        )}
        {subject?.catalog_mismatch && (
          <span className="dp-subject-fact warn">The catalog files this performance under another production or venue.</span>
        )}
      </div>
    </div>
  );
}

// ── Journey ─────────────────────────────────────────────────────────────────

function stepWhen(step: JourneyStep, nowMs: number): string {
  if (step.count === 1) return dpAge(step.first.ts, nowMs);
  return `${step.count}× · last ${dpAge(step.last.ts, nowMs)}`;
}

function StepButton({
  step,
  active,
  onSelect,
  nowMs,
  laneTenant,
}: {
  step: JourneyStep;
  active: boolean;
  onSelect: () => void;
  nowMs: number;
  laneTenant: string | null | undefined;
}) {
  const row = step.last;
  const target = row.target ? TARGET_LABEL[row.target] : null;
  const foreign = isForeign(row.tenant_id, laneTenant);
  return (
    <button
      type="button"
      className={'dp-step' + (active ? ' active' : '')}
      aria-pressed={active}
      data-lineage-id={step.id}
      onClick={onSelect}
    >
      <SyncDot band={row.stage === 'enqueue' ? 'ok' : statusBand(row.status)} />
      <span className="dp-step-name">
        {STAGE_LABEL[row.stage]}
        {target && !STAGE_LABEL[row.stage].includes(target) ? ` · ${target}` : ''}
        {foreign && <span className="dp-step-foreign"> · another tenant</span>}
      </span>
      <span className="dp-step-status">{row.stage === 'enqueue' ? 'queued' : row.status}</span>
      <span className="dp-step-when" title={at(row.ts)}>{stepWhen(step, nowMs)}</span>
    </button>
  );
}

function SourceButton({ active, onSelect, created, nowMs }: { active: boolean; onSelect: () => void; created: string; nowMs: number }) {
  return (
    <button type="button" className={'dp-step' + (active ? ' active' : '')} aria-pressed={active} data-lineage-id={SOURCE_STEP} onClick={onSelect}>
      <SyncDot band="ok" />
      <span className="dp-step-name">Created in EncorePro</span>
      <span className="dp-step-status">source</span>
      <span className="dp-step-when" title={at(created)}>{dpAge(created, nowMs)}</span>
    </button>
  );
}

function Field({ k, v }: { k: string; v: string | null | undefined }) {
  if (v == null || v === '') return null;
  return (
    <div className="dp-x-row">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

const yesNo = (v: boolean | null) => (v == null ? null : v ? 'yes' : 'no');

/** An observation recorded under another tenant than the lane's (shown, never used to name the item). */
function isForeign(tenant: string | null, lane: string | null | undefined): boolean {
  return !!tenant && !!lane && tenant.toLowerCase() !== lane.toLowerCase();
}

function StepDetail({
  step,
  kind,
  subject,
}: {
  step: JourneyStep;
  kind: LineageKind;
  subject: LineageSubject | null | undefined;
}) {
  const row = step.last;
  const named = (id: string | null, name: string | null | undefined) =>
    id == null ? null : name ? `${name} · ${id}` : id;
  return (
    <div className="dp-step-detail-body" data-testid="trace-step-detail" data-lineage-id={step.id}>
      <h4>{STAGE_LABEL[row.stage]}</h4>
      <p className="dp-step-sentence">{stepSentence(row, kind)}</p>
      {step.count > 1 && (
        <p className="dp-step-note">
          Observed {step.count} times between {at(step.first.ts)} and {at(step.last.ts)} · same values.
        </p>
      )}
      {step.changes.length > 0 && (
        <ul className="dp-step-changes">
          {step.changes.map((c) => (
            <li key={c.label}>
              {c.label}: {c.from} → {c.to} <span className="muted">(observed {at(step.first.ts)})</span>
            </li>
          ))}
        </ul>
      )}
      <div className="dp-x-sect">
        <Field k="when" v={at(row.ts)} />
        <Field k="status" v={row.stage === 'enqueue' ? 'queued' : row.status} />
        <Field k="outcome" v={reasonWords(row.outcome)} />
        <Field k="destination" v={row.target ? TARGET_LABEL[row.target] ?? row.target : null} />
        <Field k="attempt" v={row.attempt != null ? String(row.attempt) : null} />
        <Field k="HTTP" v={row.http_status != null ? String(row.http_status) : null} />
        <Field k="fbtrace_id" v={row.fbtrace_id} />
        <Field k="value" v={row.value != null ? `${row.value.toFixed(2)} ${row.currency ?? ''}`.trim() : null} />
        <Field k="units" v={row.units != null ? String(row.units) : null} />
        <Field
          k="production"
          v={named(row.production_id, row.production_id === subject?.production_id ? subject?.production_name : null)}
        />
        <Field k="venue" v={named(row.venue_id, row.venue_id === subject?.venue_id ? subject?.venue_name : null)} />
        <Field k="conversion key" v={row.order_id} />
        <Field k="pixel" v={row.pixel_id} />
        <Field k="signals" v={row.signals} />
        <Field k="UID2 tokens" v={row.uid2_token_count != null ? String(row.uid2_token_count) : null} />
        <Field k="email hash" v={yesNo(row.has_email_hash)} />
        <Field k="phone hash" v={yesNo(row.has_phone_hash)} />
        <Field k="name hash" v={yesNo(row.has_name_hash)} />
        <Field k="person" v={yesNo(row.person_id_present)} />
        <Field k="household" v={yesNo(row.household_id_present)} />
        <Field k="buyer id" v={yesNo(row.user_id_present)} />
      </div>
      {(row.failure || row.error_summary) && (
        <FailureNotice failure={row.failure} fallback={row.error_summary} dense announce={false} />
      )}
    </div>
  );
}

function QueuedDetail({
  step,
  destinations,
}: {
  step: JourneyStep | null;
  destinations: readonly LineageDestination[];
}) {
  return (
    <div className="dp-step-detail-body" data-testid="trace-step-detail" data-lineage-id={step?.id ?? 'queued'}>
      <h4>Queued</h4>
      {step && <p className="dp-step-sentence">Queued for delivery {at(step.first.ts)}.</p>}
      {destinations.length > 0 ? (
        <p className="dp-step-note">Where delivery stands now: see Deliveries below.</p>
      ) : step ? (
        <p className="dp-step-note">
          Queued {at(step.first.ts)} was recorded; its queue record is no longer available (likely collected), so the
          outcome is unknown unless a send step below records it.
        </p>
      ) : null}
    </div>
  );
}

function SourceDetail({ ticket }: { ticket: TicketCurrentState }) {
  return (
    <div className="dp-step-detail-body" data-testid="trace-step-detail" data-lineage-id={SOURCE_STEP}>
      <h4>Created in EncorePro</h4>
      <p className="dp-step-sentence">
        EncorePro created the first line {at(ticket.first_created_in_source)} and last saved a line{' '}
        {at(ticket.last_saved_in_source)}.
      </p>
      <p className="dp-step-note">This comes from the ticket as SAM holds it now, not from a recorded observation.</p>
    </div>
  );
}

// ── Ticket now ──────────────────────────────────────────────────────────────

function buyerWords(buyer: BuyerState): string[] {
  switch (buyer.state) {
    case 'no_user_id':
      return ['No buyer id on this line.'];
    case 'unavailable':
      return ['The buyer is not available in this tenant.'];
    case 'known':
      return [
        `Buyer seen ${fmtDateTime(buyer.first_seen)} – ${fmtDateTime(buyer.last_seen)} ET.`,
        buyer.person_resolved ? 'Resolved to a person.' : 'Not resolved to a person.',
        buyer.household_present ? 'Household present.' : 'No household.',
        ...uid2Words(buyer.uid2),
        buyer.processing_checkpoint
          ? `UID2 processing checkpoint ${at(buyer.processing_checkpoint)}.`
          : 'UID2 processing checkpoint: not available, re-pended or never processed.',
        buyer.retry_checkpoint ? `UID2 retry checkpoint ${at(buyer.retry_checkpoint)}.` : 'No UID2 retry checkpoint recorded.',
      ];
  }
}

function LineCard({ line, subject }: { line: TicketLineState; subject: LineageSubject | null | undefined }) {
  const production =
    line.production_id && line.production_id === subject?.production_id && subject.production_name
      ? subject.production_name
      : line.production_id;
  return (
    <li className="dp-line-card" data-testid="ticket-line">
      <div className="dp-line-hd">
        <span className={'dp-line-kind ' + line.kind}>{line.kind === 'refund' ? 'Refund line' : 'Sale line'}</span>
        {line.type_name && <span className="muted">{line.type_name}</span>}
        {line.is_free && <span className="muted">free</span>}
        {!line.amount_present && <span className="dp-line-warn">no amount</span>}
      </div>
      <div className="dp-x-sect">
        <Field k="created in source" v={at(line.created_in_source)} />
        <Field k="last saved in source" v={at(line.last_saved_in_source)} />
        <Field k="last stored in SAM" v={at(line.last_stored_in_sam)} />
        <Field k="production" v={production} />
        <Field k="person" v={line.person_resolved ? 'resolved' : 'not resolved'} />
        <Field k="household" v={line.household_present ? 'present' : 'absent'} />
      </div>
      <ul className="dp-line-buyer">
        {buyerWords(line.buyer).map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
      <CopyableId value={line.line_id} truncate />
    </li>
  );
}

function Deliveries({ destinations, nowMs }: { destinations: readonly LineageDestination[]; nowMs: number }) {
  if (!destinations.length) return null;
  return (
    <section className="dp-deliveries" aria-label="Deliveries">
      <h4>Deliveries</h4>
      <ul>
        {destinations.map((d) => {
          const outcome = destinationOutcome(d);
          return (
            <li key={d.target} className="dp-delivery" data-testid={`delivery-${d.target}`}>
              <div className="dp-delivery-hd">
                <span className="dp-delivery-name">{TARGET_LABEL[d.target] ?? d.target}</span>
                <SyncPill band={outcomeBand(outcome)} label={OUTCOME_LABEL[outcome]} />
                <span className="muted" title={at(d.updated_at)}>changed {dpAge(d.updated_at, nowMs)}</span>
              </div>
              <div className="dp-x-sect">
                <Field k="reason" v={reasonWords(d.reason)} />
                <Field k="recorded failed attempts" v={d.retry_failures != null ? String(d.retry_failures) : null} />
                <Field k="queued" v={d.created_at ? at(d.created_at) : null} />
                <Field k="next attempt" v={d.next_attempt_at ? at(d.next_attempt_at) : null} />
              </div>
              {d.failure && d.failure_kind === 'resolved' ? (
                <details className="dp-delivery-history">
                  <summary>Earlier error (resolved)</summary>
                  <FailureNotice failure={d.failure} dense announce={false} tone="warn" />
                </details>
              ) : d.failure ? (
                <FailureNotice failure={d.failure} dense announce={false} tone={d.failure_kind === 'retrying' ? 'warn' : 'bad'} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function TicketNow({
  data,
  onRefresh,
  refreshing,
}: {
  data: PipelineLineageResponse;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const ticket = data.ticket;
  const lookup = data.ticket_lookup;
  const [showAll, setShowAll] = useState(false);
  if (!lookup) return null;
  const cards = ticket ? (showAll ? ticket.lines : ticket.lines.slice(0, FIRST_LINE_CARDS)) : [];
  return (
    <section className="dp-ticket-now" aria-label="Ticket now">
      <div className="dp-ticket-now-hd">
        <h4>Ticket now</h4>
        <span className="muted">as of {at(data.as_of)}</span>
        <button type="button" className="btn btn-sm" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {lookup === 'not_found' && <p className="dp-step-note">No line with this ticket id is stored for this tenant.</p>}
      {lookup === 'incomplete' && (
        <p className="dp-step-note">This ticket has more lines than a trace reads, so no partial picture is shown.</p>
      )}
      {lookup === 'no_tenant' && (
        <p className="dp-step-note">This deployment names no EncorePro tenant, so the ticket's lines cannot be read.</p>
      )}
      {lookup === 'not_a_uuid' && <p className="dp-step-note">This id is not a ticket id, so it names no stored ticket.</p>}
      {ticket && (
        <>
          <ul className="dp-lines">
            {cards.map((line) => (
              <LineCard key={line.line_id} line={line} subject={data.subject} />
            ))}
          </ul>
          {cards.length < ticket.lines.length && (
            <button type="button" className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setShowAll(true)}>
              Show all {ticket.lines.length} lines
            </button>
          )}
          {ticket.lines_truncated && (
            <p className="dp-step-note">
              Showing {ticket.lines.length} of {ticket.lines_total} lines; the counts and the verdict above cover all of
              them.
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ── Footer and empty states ─────────────────────────────────────────────────

function EvidenceFooter({ data }: { data: PipelineLineageResponse }) {
  const w = data.window;
  if (!w) return null;
  const rows = data.foreign_tenant_rows;
  const queue = data.foreign_tenant_destinations;
  return (
    <footer className="dp-evidence" data-testid="trace-evidence">
      {w.returned > 0 && w.earliest_returned && w.latest_returned
        ? `Showing ${plural(w.returned, 'recorded observation')} from ${at(w.earliest_returned)} to ${at(w.latest_returned)}; `
        : 'No recorded observations; '}
      the window starts {at(w.query_floor)}.
      {w.truncated && ' Older observations exist but aren’t shown.'} Observations are best-effort, so a missing one is not
      proof something didn’t happen.
      {rows > 0 && ` ${plural(rows, 'observation')} recorded under another tenant ${rows === 1 ? 'is' : 'are'} marked in the journey and not used to name the item.`}
      {queue > 0 && ` ${plural(queue, 'queue record')} from another tenant ${queue === 1 ? 'was' : 'were'} left out.`}
    </footer>
  );
}

function emptyMessage(data: PipelineLineageResponse, correlationId: string): string | null {
  if (data.rows.length > 0) return null;
  if (data.destinations.length > 0)
    return 'No observations are retained for this item; the queue still holds it, so Deliveries below is the evidence.';
  switch (data.ticket_lookup) {
    case 'found':
      return 'This ticket is stored, but no observation of its journey is retained (older than 30 days, or never traced).';
    case 'not_found':
      return `Nothing found for ${correlationId}: no observation in the last 30 days, no queue row and no stored ticket.`;
    case 'incomplete':
    case 'no_tenant':
    case 'not_a_uuid':
      // The ticket could not be read, which is not the same as it not existing.
      return 'No observation in the last 30 days and no queue row; the stored ticket could not be read (see Ticket now).';
    default:
      return `Nothing found for ${correlationId}: no observation in the last 30 days and no queue row.`;
  }
}

// ── The panel ───────────────────────────────────────────────────────────────

export function LineageTrace({
  correlationId,
  query,
  onExit,
  onFocusNode,
  pick,
}: {
  correlationId: string;
  query: UseQueryResult<PipelineLineageResponse>;
  onExit: () => void;
  /** The diagram node of the selected step, for its focus ring. */
  onFocusNode: (node: DpNodeId | null) => void;
  /** A node clicked in the diagram while tracing: select that node's newest step. */
  pick: { node: DpNodeId; n: number } | null;
}) {
  const nowMs = useNowMs(5000);
  const adaptive = useAdaptive();
  const kind = kindFromCorrelationId(correlationId);
  const data = query.data;
  const steps = useMemo(() => foldJourney(data?.rows ?? [], kind), [data?.rows, kind]);
  const destinations = data?.destinations ?? [];
  const ticket = data?.ticket ?? null;
  const hasSource = kind === 'ticket' && !!ticket?.lines.length;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The newest step until one is chosen; a chosen step stays chosen while it exists.
  const selected =
    (selectedId === SOURCE_STEP && hasSource ? SOURCE_STEP : steps.find((s) => s.id === selectedId)?.id) ??
    steps.at(-1)?.id ??
    (hasSource ? SOURCE_STEP : null);
  const selectedStep = steps.find((s) => s.id === selected) ?? null;

  useEffect(() => {
    onFocusNode(selectedStep ? stageNode(selectedStep.stage, kind) : selected === SOURCE_STEP ? 'encore-pg' : null);
  }, [selectedStep, selected, kind, onFocusNode]);
  useEffect(() => () => onFocusNode(null), [onFocusNode]);
  useEffect(() => {
    if (!pick) return;
    const newest = [...steps].reverse().find((s) => stageNode(s.stage, kind) === pick.node);
    if (newest) setSelectedId(newest.id);
    else if (pick.node === 'encore-pg' && hasSource) setSelectedId(SOURCE_STEP);
    // Only a new pick selects; steps changing under an old pick do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick]);

  const state = traceState(data);
  const forbidden = query.error instanceof ApiError && query.error.status === 403;

  const detail = (id: string | null) => {
    if (id === SOURCE_STEP && ticket) return <SourceDetail ticket={ticket} />;
    const step = steps.find((s) => s.id === id);
    if (!step) return null;
    if (step.stage === 'enqueue') return <QueuedDetail step={step} destinations={destinations} />;
    return <StepDetail step={step} kind={kind} subject={data?.subject} />;
  };

  const journey = (
    <ol className="dp-journey-list" aria-label="Journey">
      {JOURNEY_PHASES.map((phase) => {
        const own = steps.filter((s) => s.phase === phase);
        const source = phase === 'source' && hasSource && ticket?.lines[0];
        if (!own.length && !source) return null;
        return (
          <li key={phase} className="dp-phase">
            <div className="dp-phase-label">{phaseLabel(phase, kind)}</div>
            <ul>
              {source && (
                <li>
                  <SourceButton
                    active={selected === SOURCE_STEP}
                    onSelect={() => setSelectedId(SOURCE_STEP)}
                    created={ticket?.first_created_in_source ?? source.created_in_source}
                    nowMs={nowMs}
                  />
                  {adaptive && selected === SOURCE_STEP && <div className="dp-step-inline">{detail(SOURCE_STEP)}</div>}
                </li>
              )}
              {own.map((step) => (
                <li key={step.id}>
                  <StepButton step={step} active={step.id === selected} onSelect={() => setSelectedId(step.id)} nowMs={nowMs} laneTenant={data?.lane_tenant} />
                  {adaptive && step.id === selected && <div className="dp-step-inline">{detail(step.id)}</div>}
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ol>
  );

  const empty = data ? emptyMessage(data, correlationId) : null;

  return (
    <section className="panel dp-context dp-trace-panel" aria-label={kind === 'ticket' ? 'Ticket journey' : 'Event journey'}>
      <header className="dp-trace-hd">
        <div className="dp-trace-hd-1">
          <h3>{kind === 'ticket' ? 'Ticket journey' : 'Event journey'}</h3>
          {data && <OutcomeChip destinations={destinations} />}
          <button type="button" className="dp-x-btn" onClick={onExit} title="Close the trace" aria-label="Close the trace">
            {Icons.x}
          </button>
        </div>
        <div className="dp-trace-hd-2">
          <CopyableId value={correlationId} truncate />
        </div>
      </header>
      {data && <SubjectStrip subject={data.subject} ticket={ticket} kind={kind} />}
      <div className="dp-context-body">
        {query.isLoading && !data ? (
          <div className="dp-empty">Tracing {correlationId}…</div>
        ) : forbidden ? (
          <div className="dp-empty">
            This item names a production outside your access, so its journey is not shown.
          </div>
        ) : query.isError && !data ? (
          // The read FAILED: never present an infrastructure error as "no lineage".
          <QueryErrorNote error={query.error} what={`the journey of ${correlationId}`} onRetry={() => void query.refetch()} />
        ) : data ? (
          <>
            {empty && <div className="dp-empty dp-trace-empty">{empty}</div>}
            {data.rows.length > 0 || hasSource ? (
              <div className={'dp-journey' + (adaptive ? ' one-col' : '')}>
                {journey}
                {!adaptive && (
                  <aside className="dp-step-detail" aria-label="Step detail">
                    {detail(selected)}
                  </aside>
                )}
              </div>
            ) : null}
            {state === 'in_flight' && <p className="dp-step-note">Still in flight: SAM checks again every few seconds.</p>}
            {state === 'awaiting_enqueue' && (
              <p className="dp-step-note">
                {kind === 'ticket'
                  ? 'Not queued for delivery yet. Tickets are queued in batches every few hours; checking once a minute.'
                  : 'Not queued for delivery yet; checking once a minute.'}
              </p>
            )}
            {destinations.length === 0 && data.rows.length > 0 && (
              <p className="dp-step-note">
                {data.rows.some((r) => r.stage === 'meta_send' || r.stage === 'ttd_send')
                  ? 'The queue no longer holds this item (likely collected); the send steps above are what was recorded.'
                  : 'No retained queue evidence and no recorded send; the historical delivery outcome is unknown.'}
              </p>
            )}
            <Deliveries destinations={destinations} nowMs={nowMs} />
            <TicketNow data={data} onRefresh={() => void query.refetch()} refreshing={query.isFetching} />
            <EvidenceFooter data={data} />
          </>
        ) : null}
      </div>
    </section>
  );
}
